import React from 'react';
import { connect, ConnectedProps, useDispatch } from 'react-redux';
import { Switch, Redirect, RouteComponentProps } from 'react-router';
import { Trans, withTranslation } from 'react-i18next';

import Route, {
  loadable,
  PermissionRoute,
} from '../../common/components/route';
import Spinner from '../../common/components/spinner';
import ExpirationWall from '../../common/components/expiration-wall';
import ReportTemplate from '../report/template';
import LegalDocument from './components/legal-document';
import UpgradePlanPackageModal from '@common/components/upgrade-plan-package-modal/upgrade-plan-package-modal';
import PagePrint from '@modules/documents/containers/page-print';
import { usePushNotifications } from '@common/hooks/use-push-notifications';
import { FilterPersistenceProvider, FilterPersistenceClearOnChange } from '@common/contexts/filter-persistence';
import { CustomUserPropertiesProvider }
from '@modules/user-properties/contexts/custom-user-properties-context';

import * as organisationSelector from '../organisation/selectors/organisation';
import { buildingPath, isTrialBuilding } from '../authentication/utils/trial-route';
import * as userSelector from './selectors/logged-user';
import { ECompanyDashboardPermissions } from '../../common/definitions';
import type { StoreState } from '../../common/types/store';
import { HIDE_PLAN_PACKAGE_UPGRADE_MODAL } from './actions';


require('./styles.scss');

// Routes
const NetworkTemplate = loadable(() => import('../network/template'));
const ChannelShortLinkRedirect = loadable(() => import('../social/containers/channel-short-link-redirect'));
const ShortcutShortLinkRedirect = loadable(() => import('../social/containers/shortcut-short-link-redirect'));
const AdminTemplate = loadable(() => import('../admin/template'));

const documentLookup: Record<string, string> = {
  privacy: 'https://assets.oneteam.io/globals/statements/(locale)/privacy-policy.html',
};

const mapStateToProps = (state: StoreState) => ({
  initialised: state.app.organisationInitialised,
  planPackageModal: state.ui.planPackageModal,
  loggedUser: userSelector.selected(state),
  organisation: organisationSelector.selected(state),
  documents: userSelector.documentsToAccept(state),
  isDemoExpired: organisationSelector.isDemoExpired(state),
  permissions: state.permissions,
});

const mapDispatchToProps = {
  initialiseCore: require('./actions/initialise').default,
  logout: require('../authentication/actions/logout').default,
  acceptDocument: require('./actions/accept-document').default,
};

type RouteProps = RouteComponentProps;

type Props = RouteProps & ConnectedProps<typeof connector>;

const connector = connect(mapStateToProps, mapDispatchToProps);

const CoreTemplate = ({
  history,
  initialised,
  isDemoExpired,
  loggedUser,
  organisation,
  documents,
  permissions,
  initialiseCore,
  acceptDocument,
  logout,
  planPackageModal,
}: Props) => {
  const dispatch = useDispatch();

  // Initialize push notifications using react-onesignal
  // This runs after the app is fully initialized with a valid user ID
  usePushNotifications();

  // Initialise application
  React.useEffect(() => {
    if (!organisation) return history.replace('/auth/organisations');


    (async () => {
      try {
        await initialiseCore(history);
      } catch (err) {
        history.push('/auth/organisations');
      }
    })();

    return () => {
      if (window.Intercom) window.Intercom('shutdown');

    };
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      history.push('/auth/login');
    }
  };

  if (!initialised) {
    return (
      <div className="tw-h-screen tw-flex tw-items-center tw-justify-center">
        <div className="AsyncSpinner">
          <div>
            <Trans i18nKey="core:template_loading" />
            <Spinner size="large" centered />
          </div>
        </div>
      </div>
    );
  }

  // logged user might not exist in SSR
  const docLocale = loggedUser?.language?.locale === 'nl' ? 'nl' : 'en';
  // Take the first document in queue, so we display 1 document at a time
  const documentToDisplay = (
    documents[0] &&
    documentLookup[documents[0]].replace('(locale)', docLocale)
  );
  const isAdmin = ECompanyDashboardPermissions.some((permission) => permissions.organisation.includes(permission));

  if (isTrialBuilding(organisation)) {
    return <Redirect to={buildingPath(organisation.id)} />;
  }

  return (
    <FilterPersistenceProvider>
      <FilterPersistenceClearOnChange />
      <CustomUserPropertiesProvider
        organisationId={organisation?.id}
        enabled={!!organisation?.id}
      >
        <div id="Core">
          {documentToDisplay && (
          <LegalDocument
            key={documentToDisplay}
            // @ts-expect-error
            path={documentToDisplay}
            onReject={() => handleLogout()}
            onAccept={() => acceptDocument(documents[0])}
          />
        )}
          {isDemoExpired && (
          // @ts-expect-error
          <ExpirationWall organisation={organisation} />
        )}
          {planPackageModal && (
          <UpgradePlanPackageModal
            show
            onHide={() => dispatch({ type: HIDE_PLAN_PACKAGE_UPGRADE_MODAL })}
            {...planPackageModal}
          />
        )}
          <Switch>
            <Route
              path={['/channel/:channelId', '/channels/:channelId']}
              exact
              component={ChannelShortLinkRedirect}
            />
            <Route
              path="/shortcuts/:shortcutId"
              exact
              component={ShortcutShortLinkRedirect}
            />
            <Route
              path="/networks/:networkId/documents/page/:pageId/print"
              render={({ match }) => <Redirect to={`/documents/page/${match.params.pageId}/print`} />}
            />
            <Route
              path={['/documents/page/:pageId/print', '/admin/documents/page/:pageId/print']}
              component={PagePrint}
            />
            <Route path="/report" component={ReportTemplate} />
            {isAdmin && (
            <PermissionRoute
              path="/admin"
              component={AdminTemplate}
              permission={{ name: ECompanyDashboardPermissions }}
            />
          )}
            {isAdmin && <Redirect exact from="/" to="/admin" />}
            {/* Render NetworkTemplate for baseline routes or when network is selected */}
            <Route path="/" component={NetworkTemplate} />
            <Redirect to="/auth/organisations" />
          </Switch>
        </div>
      </CustomUserPropertiesProvider>
    </FilterPersistenceProvider>
  );
};

export default withTranslation()(connector(CoreTemplate));
